import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DarkModeToggle from '../ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';
import { useFrontAuthStore } from '../../auth/store/authStore';

interface SubMenuItem {
  label: string;
  key: string;
  icon?: string;
  /** 클릭 시 이동할 경로 */
  path?: string;
}

interface MenuItem {
  label: string;
  key: string;
  icon?: string;
  /** 클릭 시 이동할 경로 */
  path?: string;
  /** 활성화 상태 강제 지정 */
  active?: boolean;
  children?: SubMenuItem[];
}

interface SubLayoutProps {
  children: React.ReactNode;
  title?: string;
  menuItems?: MenuItem[];
  activeMenu?: string;
  onMenuClick?: (key: string) => void;
  contentAlign?: 'left' | 'center' | 'right';
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background:
    url('/images/global/member_bg.png') no=repeat center center;
  background-size: cover;

  html.dark-mode & {
    background:
      url('/images/global/member_bg_dark.png') no-repeat center center;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.xl};
  flex-shrink: 0;
`;

const Logo = styled.div<{ $isDark?: boolean }>`
  cursor: pointer;
  img {
    transition: filter 0.3s ease;
    ${props => props.$isDark && `filter: invert(1);`}
  }
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const SideMenu = styled.nav`
  width: 220px;
  flex-shrink: 0;
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const MenuItemButton = styled.button<{ $active: boolean; $hasChildren?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.$active ? 'var(--bg-secondary)' : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.book.color9b : props.theme.colors.book.color1b};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
  outline: none;

  & img {
    width: 20px;
    height: 20px;
  }
  &:hover {
    background-color: rgba(255, 255, 255, 0.6);
  }
  html.dark-mode & img {
    filter: invert(1);
  }
`;

const MenuLabel = styled.span`
  flex: 1;
`;

const ArrowIcon = styled.span<{ $expanded: boolean }>`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: ${props => props.$expanded ? 'rotate(-135deg)' : 'rotate(45deg)'};
  transition: transform 0.2s;
  margin-left: auto;
`;

const SubMenuList = styled.div<{ $expanded: boolean }>`
  max-height: ${props => props.$expanded ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const SubMenuButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  padding-left: ${props => props.theme.spacing['3xl']};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.$active ? props.theme.colors.book.color4b : props.theme.colors.book.color6b};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.$active ? props.theme.fontWeight.medium : props.theme.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
  outline: none;
  
  &::before {
    content: '';
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: ${props => props.$active ? props.theme.colors.book.color4b : '#d9d9d9'};
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.4);
  }

  &.logout {
    font-size: ${props => props.theme.fontSize.sm};
    color: ${props => props.theme.colors.book.color6b};
    font-weight: ${props => props.theme.fontWeight.normal};
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover {
      background-color: transparent;
    }
  }
`;

const ContentArea = styled.main`
  flex: 1;
  display: flex;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};
  min-height: 0;
  overflow-y: auto;
`;

const ALIGN_MAP = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
} as const;

const ContentInner = styled.div<{ $align: 'left' | 'center' | 'right' }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${props => ALIGN_MAP[props.$align]};
`;

const SubLayout: React.FC<SubLayoutProps> = ({
  children,
  title,
  menuItems = [],
  activeMenu,
  onMenuClick,
  contentAlign = 'center',
}) => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { isAuthenticated } = useFrontAuthStore();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    menuItems.forEach(item => {
      if (item.children?.some(sub => sub.key === activeMenu)) {
        initial.add(item.key);
      }
    });
    return initial;
  });

  const toggleExpand = (key: string) => {
    setExpandedMenus(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleMenuItemClick = (item: MenuItem | SubMenuItem, hasChildren = false) => {
    if (hasChildren) {
      toggleExpand(item.key);
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
    onMenuClick?.(item.key);
  };

  return (
    <Container>
      <Header>
        <Logo $isDark={isDarkMode} onClick={() => navigate(isAuthenticated ? '/book/list' : '/')}>
          <img src="/images/global/logo.svg" alt="logo" />
        </Logo>
        <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
      </Header>
      <Body>
        {menuItems.length > 0 && (
          <SideMenu>
            {title && (
              <div style={{ padding: '8px 12px', fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>
                {title}
              </div>
            )}
            {menuItems.map((item) => {
              const hasChildren = !!(item.children && item.children.length > 0);
              const isActive = item.active ?? (!hasChildren && activeMenu === item.key);
              const isParentActive = hasChildren && item.children!.some(sub => activeMenu === sub.key);
              return (
                <div key={item.key}>
                  <MenuItemButton
                    $active={isActive || isParentActive}
                    $hasChildren={hasChildren}
                    onClick={() => handleMenuItemClick(item, hasChildren)}
                  >
                    {item.icon && <img src={item.icon} alt="" width={16} height={16} />}
                    <MenuLabel>{item.label}</MenuLabel>
                    {hasChildren && (
                      <ArrowIcon $expanded={expandedMenus.has(item.key)} />
                    )}
                  </MenuItemButton>
                  {hasChildren && (
                    <SubMenuList $expanded={expandedMenus.has(item.key)}>
                      {item.children!.map((sub) => (
                        <SubMenuButton
                          key={sub.key}
                          $active={activeMenu === sub.key}
                          onClick={() => handleMenuItemClick(sub)}
                        >
                          {sub.icon && <img src={sub.icon} alt="" width={14} height={14} />}
                          {sub.label}
                        </SubMenuButton>
                      ))}
                    </SubMenuList>
                  )}
                </div>
              );
            })}
          </SideMenu>
        )}
        <ContentArea>
          <ContentInner $align={contentAlign}>
            {children}
          </ContentInner>
        </ContentArea>
      </Body>
    </Container>
  );
};

export default SubLayout;
